import React from 'react';
import { ExtendedTheme, Heading, TextBlock, Button } from 'neon-design-system';
import { createStyles, makeStyles } from '@material-ui/core';
import { usePreloadImage } from '../hooks/use-preload-image';
import { useUpBreakpoints } from '../providers/BreakpointsProvider';
import LinkList, { ListItemOrientation, ListItemType } from './LinkList';

interface StyleProps {
	imageUrl?: string;
}

const useStyles = makeStyles<ExtendedTheme, StyleProps>((theme: ExtendedTheme) => createStyles({
	root: {
		marginBottom: theme.spacing(4),
		[theme.breakpoints.up('md')]: {
			marginBottom: theme.spacing(2)
		}
	},
	topicInfoWrapper: {
		display: 'flex',
		zIndex: 1,
		flexDirection: 'column-reverse',
		alignItems: 'center',
		marginTop: theme.spacing(4),
		[theme.breakpoints.up('sm')]: {
			marginTop: 0,
			flexDirection: 'row',
			justifyContent: 'space-between'
		}
	},
	topicInfo: {
		maxWidth: '100%',
		display: 'flex',
		flex: '1 1 auto',
		flexDirection: 'column',
		alignItems: 'center',
		[theme.breakpoints.up('sm')]: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'flex-start'
		},
		[theme.breakpoints.only('xs')]: {
			'& > p': {
				marginBottom: 0
			}
		}
	},
	description: {
		maxWidth: '100%',
		textAlign: 'center',
		marginBottom: theme.spacing(4),
		[theme.breakpoints.up('sm')]: {
			maxWidth: 700,
			marginBottom: 0,
			textAlign: 'left'
		},
		[theme.breakpoints.up('xl')]: {
			maxWidth: 720
		}
	},
	image: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-end',
		height: 200,
		padding: theme.spacing(2),
		marginBottom: theme.spacing(1),
		backgroundColor: theme.palette.grey[400],
		borderRadius: 8,
		transition: 'height 300ms ease-out',
		overflow: 'hidden',
		'&::before': {
			position: 'absolute',
			top: 0,
			bottom: 0,
			left: 0,
			right: 0,
			content: '""',
			// If this function is moved to wrap the outer selector, the :before style is applied muliple times.
			// Seems to be related to https://github.com/mui-org/material-ui/issues/17126
			opacity: (props: StyleProps) => props.imageUrl ? 1 : 0,
			transition: 'opacity 0.3s ease-out',
			backgroundImage: (props: StyleProps) => props.imageUrl && `url('${props.imageUrl}')`,
			backgroundSize: 'cover',
			backgroundPosition: 'center'
		},
		'&::after': {
			position: 'absolute',
			top: 0,
			bottom: 0,
			left: 0,
			right: 0,
			content: '""',
			backgroundImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.8) 100%)'
		},
		[theme.breakpoints.up('sm')]: {
			height: 280
		},
		[theme.breakpoints.up('md')]: {
			height: 320,
			padding: theme.spacing(4)
		},
		[theme.breakpoints.up('xl')]: {
			height: 360
		}
	},
	imageTitle: {
		zIndex: 1,
		textAlign: 'center',
		[theme.breakpoints.up('sm')]: {
			textAlign: 'left',
			marginBottom: theme.spacing(1)
		}
	},
	categoryListWrapper: {
		marginTop: theme.spacing(1.9),
		[theme.breakpoints.up('md')]: {
			display: 'none'
		},
		[theme.breakpoints.only('xs')]: {
			display: 'flex',
			justifyContent: 'center'
		}
	}
}));

const useSubscribeButtonStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	subscribeBtnWrapper: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginBottom: theme.spacing(4),
		[theme.breakpoints.up('sm')]: {
			marginBottom: 0,
			marginLeft: theme.spacing(2),
			minWidth: 144
		}
	}
}));

const SubscribeButton: React.FC<{ id: string; name: string; isSubscribed: boolean; modifySubscription: () => void }>
	= ({ id, name, isSubscribed, modifySubscription }) => {

		const classes = useSubscribeButtonStyles();

		return (
			<div className={classes.subscribeBtnWrapper}>
				<Button
					size="large"
					id={'button-subscribe'}
					colorType={isSubscribed ? 'grey' : 'primary'}
					onClick={() => modifySubscription()}
				>

					{isSubscribed ? 'Unfollow' : 'Follow'}
				</Button>
			</div>
		);

	};

export interface CategoryCoverProps {
	id: string;
	name: string;
	description: string;
	imageUrl?: string;
	isSubscribed: boolean;
	modifySubscription: () => void;
	categories: {
		id: string;
		name: string;
		url: string;
	}[];
}

const CategoryCover: React.FC<CategoryCoverProps> = ({ id, name, description, imageUrl, categories, isSubscribed, modifySubscription }) => {
	const preloadedImage = usePreloadImage(imageUrl);
	const classes = useStyles({ imageUrl: preloadedImage });
	const { sm } = useUpBreakpoints();

	const descriptionAndSubscribe =
		<div className={classes.topicInfoWrapper}>
			<div id="category-info" className={classes.topicInfo}>
				<TextBlock
					xs="small"
					md="medium"
					xl="large"
					className={classes.description}
					styleName="largeBody2"
					color="grey"
					shade={200}
				>
					{description}
				</TextBlock>
			</div>
			<SubscribeButton id={id} name={name} isSubscribed={isSubscribed} modifySubscription={modifySubscription}/>
		</div>

	return (
		<div className={classes.root}>
			<div id="category-cover-image" className={classes.image}>
				<Heading
					styleName="largeTitle"
					className={classes.imageTitle}
					color="white"
					md="medium"
					xl="large"
					xs="small"
				>
					{name}
				</Heading>
				{sm ? descriptionAndSubscribe : undefined}
			</div>
			{!sm ? descriptionAndSubscribe : undefined}
			{
				(!!categories.length) && (
					<div className={classes.categoryListWrapper}>
						<LinkList
							items={categories}
							orientation={ListItemOrientation.Horizontal}
							displayType={ListItemType.Chip}
							chipVariant={'info'}
						/>
					</div>
				)
			}
		</div>
	);
};
export default CategoryCover;
